/**
 * The overview before its data has landed: the same panels, in their loading
 * state, in the order the loaded screen draws them.
 *
 * @module components/overview/OverviewLoading
 */

import React from "react";

import { PageHeader } from "@/components/ui";
import type { AuthorizationSubject } from "@/server/authorization";

import { AttentionQueue } from "./AttentionQueue";
import { ModuleReviewTable } from "./ModuleReviewTable";
import { PlatformOperations } from "./PlatformOperations";
import { PortfolioMetrics } from "./PortfolioMetrics";
import { ProgrammeDirectory } from "./ProgrammeDirectory";

export interface OverviewLoadingProps {
  /** Absent before the session resolves, when no role-specific panel is known yet. */
  subject?: AuthorizationSubject;
}

/**
 * @example
 * ```tsx
 * <OverviewLoading subject={subject} />
 * ```
 */
export function OverviewLoading({ subject = undefined }: OverviewLoadingProps): React.ReactElement {
  if (subject?.accountRole === "platform-admin") {
    return (
      <div className="flex flex-col gap-6" data-testid="overview-loading">
        <PageHeader
          title="Platform operations"
          description="See all tenants and the work still to finish."
        />
        <PlatformOperations overview={null} />
      </div>
    );
  }

  const administers = subject?.accountRole === "client-admin";
  const leads = (subject?.responsibilities ?? []).some(
    (assignment) => assignment.responsibility === "lead",
  );
  return (
    <div className="flex flex-col gap-6" data-testid="overview-loading">
      <PageHeader
        title="Overview"
        description="See what needs your attention and the status of your programmes."
      />
      <AttentionQueue sections={[]} loading />
      {(administers || leads) && <ModuleReviewTable rows={[]} loading administers={administers} />}
      {administers && <PortfolioMetrics portfolio={null} programmes={[]} users={[]} />}
      <ProgrammeDirectory
        title={administers ? "Programme directory" : "Your programmes and modules"}
        programmes={[]}
        loading
      />
    </div>
  );
}
